import { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { APIProvider } from "@vis.gl/react-google-maps";
import { Dialog, DialogContent } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { ChevronLeft, ChevronRight, User, Fuel, Gauge, ThermometerSun, Music, BatteryCharging, Users, Calendar, Star, MapPin, Building, Clock, Sparkles, Wallet, Banknote, Hourglass, Eye, LogIn, ArrowLeft } from "lucide-react";
import api, { resolveImageUrl } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import LocationPicker from "./LocationPicker";
import RouteInfoBadge from "./RouteInfoBadge";

const MAPS_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

const VEHICLE_TYPE_LABELS = { sedan: "Berline", suv: "SUV", "4x4": "4x4", minibus: "Minibus", pickup: "Pick-up" };
const FALLBACK_IMAGES = {
  sedan: "https://images.unsplash.com/photo-1622850780969-b7547252bccc?w=600&h=400&fit=crop",
  suv: "https://images.unsplash.com/photo-1519641471654-76ce0107ad1b?w=600&h=400&fit=crop",
  "4x4": "https://images.unsplash.com/photo-1519641471654-76ce0107ad1b?w=600&h=400&fit=crop",
  minibus: "https://images.unsplash.com/photo-1570125909232-eb263c188f7e?w=600&h=400&fit=crop",
  pickup: "https://images.unsplash.com/photo-1559416523-140ddc3d238c?w=600&h=400&fit=crop",
};
const CAMEROON_CITIES = ["Yaounde", "Douala", "Bafoussam", "Bamenda", "Garoua", "Maroua", "Ngaoundere", "Bertoua", "Ebolowa", "Kribi", "Limbe", "Buea", "Kumba", "Nkongsamba", "Edea", "Loum", "Dschang", "Foumban", "Kousseri", "Mokolo"];

function resolveImg(url) {
  if (!url) return null;
  if (url.includes("example.com")) return null;
  return resolveImageUrl(url);
}
function getVehicleImages(v) {
  const raw = v.image_urls?.length > 0 ? v.image_urls : (v.image_url ? [v.image_url] : []);
  const resolved = raw.map(resolveImg).filter(Boolean);
  return resolved.length > 0 ? resolved : [FALLBACK_IMAGES[v.vehicle_type] || FALLBACK_IMAGES.sedan];
}
function conditionLabel(c) { return c === "neuf" ? "Neuf" : c === "bon_etat" ? "Bon etat" : "Acceptable"; }
function conditionColor(c) { return c === "neuf" ? "bg-[#10B981]/10 text-[#10B981] border-[#10B981]/20" : c === "bon_etat" ? "bg-[#FACC15]/10 text-[#EAB308] border-[#FACC15]/20" : "bg-[#64748B]/10 text-[#64748B] border-[#64748B]/20"; }

function FeatureCard({ icon, label, value, highlight }) {
  return (
    <div className={`p-2 rounded-lg border text-center ${highlight ? "bg-[#10B981]/5 border-[#10B981]/20" : "bg-[#F8FAFC] border-[#CBD5E1]"}`}>
      <div className="flex justify-center text-[#64748B] mb-1">{icon}</div>
      <p className="text-[10px] text-[#64748B]">{label}</p>
      <p className="text-xs font-semibold text-[#0A2540]">{value}</p>
    </div>
  );
}

const RENTAL_MODES = [
  { id: "city_tour", label: "Location ville", icon: Building, suffix: "/jour" },
  { id: "out_of_city", label: "Sortie de ville", icon: MapPin, suffix: "/jour" },
  { id: "hourly", label: "Tour a l'heure", icon: Clock, suffix: "/heure" },
];

export default function VehicleDetailModal({ vehicle, open, onClose, onRent, loading, showRentButton, paymentMethods, activePacks }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const openedFromShareLink =
    !!vehicle && (searchParams.get("vehicle") === vehicle.id || searchParams.get("highlight") === vehicle.id);
  const handleBackToList = () => {
    // Remove the deep-link params so the modal closes and the list is revealed.
    const next = new URLSearchParams(searchParams);
    next.delete("vehicle");
    next.delete("highlight");
    setSearchParams(next, { replace: true });
    onClose?.();
  };
  const { user } = useAuth();
  const v = vehicle;
  const [currentImg, setCurrentImg] = useState(0);
  const [rentalMode, setRentalMode] = useState("");
  const [rentalHours, setRentalHours] = useState(1);
  const [rentalDays, setRentalDays] = useState(1);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [startTime, setStartTime] = useState("");
  const [endTime, setEndTime] = useState("");
  const [departureCity, setDepartureCity] = useState("");
  const [destinationCity, setDestinationCity] = useState("");
  const [customDeparture, setCustomDeparture] = useState("");
  const [customDestination, setCustomDestination] = useState("");
  const [pickupNeighborhood, setPickupNeighborhood] = useState("");
  const [pickupAddress, setPickupAddress] = useState("");
  // Geo-selection (LocationPicker): {address, lat, lng} - added on top of the
  // free-text fields so drivers can navigate to a precise GPS point.
  const [pickupGeo, setPickupGeo] = useState(null);
  const [dropoffGeo, setDropoffGeo] = useState(null);
  // Live-computed route between the two GPS points (km + min). Purely
  // informational — the rental price stays time-based on the server.
  const [routeInfo, setRouteInfo] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState(""); // "" = pay at pickup, "tft" = pay via TFT wallet
  // Hourly rental: destination preset (airport_douala/airport_yaounde → 20 000 F flat, or "other" → free-text)
  const [hourlyDest, setHourlyDest] = useState(""); // "", "airport_douala", "airport_yaounde", "other"
  const [hourlyDestCustom, setHourlyDestCustom] = useState("");
  const [welcomePromoActive, setWelcomePromoActive] = useState(true); // optimistic; refined by /promo/welcome/public
  // Custom admin-created promo code (client-entered). When applied, it replaces the welcome promo.
  const [promoCodeInput, setPromoCodeInput] = useState("");
  const [promoCodeApplied, setPromoCodeApplied] = useState(null); // {code, name, percent, discount}
  const [promoCodeError, setPromoCodeError] = useState("");
  const [promoCodeChecking, setPromoCodeChecking] = useState(false);

  useEffect(() => {
    // Fetch the global welcome-promo status once. If admin disabled the promo
    // OR the global end-date is passed, we hide the -15% preview entirely.
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/promo/welcome/public");
        if (!cancelled) setWelcomePromoActive(!!data?.active);
      } catch {
        if (!cancelled) setWelcomePromoActive(true); // fail-open (backend still gates at booking time)
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    setCurrentImg(0); setRentalMode(""); setRentalHours(1); setRentalDays(1);
    setStartDate(""); setEndDate(""); setStartTime(""); setEndTime(""); setDepartureCity(""); setDestinationCity("");
    setCustomDeparture(""); setCustomDestination(""); setPickupNeighborhood(""); setPickupAddress(""); setPaymentMethod("");
    setHourlyDest(""); setHourlyDestCustom("");
  }, [vehicle?.id]);

  if (!v) return null;

  const allImages = getVehicleImages(v);
  const videoSrc = v.video_url ? resolveImageUrl(v.video_url) : null;
  const prev = () => setCurrentImg(i => (i > 0 ? i - 1 : allImages.length - 1));
  const next = () => setCurrentImg(i => (i < allImages.length - 1 ? i + 1 : 0));

  const cityPrice = v.price_city_tour > 0 ? v.price_city_tour : null;
  const outPrice = v.price_out_of_city > 0 ? v.price_out_of_city : null;
  // Auto-compute hourly: prix_ville / 6, arrondi au multiple de 5000 superieur (only if cityPrice exists)
  const autoHourPrice = cityPrice ? Math.ceil((cityPrice / 6) / 5000) * 5000 : null;
  const hourPrice = v.price_per_hour > 0 ? v.price_per_hour : autoHourPrice;

  const getPrice = (mode) => {
    if (mode === "city_tour") return cityPrice;
    if (mode === "out_of_city") return outPrice;
    if (mode === "hourly") return hourPrice;
    return v.price_per_day;
  };

  const isModeAvailable = (mode) => {
    const p = getPrice(mode);
    return typeof p === "number" && p > 0;
  };

  const isAirportHourly = rentalMode === "hourly" && (hourlyDest === "airport_douala" || hourlyDest === "airport_yaounde");
  const AIRPORT_FLAT_FEE = 20000;
  const totalAmount = rentalMode === "hourly"
    ? (isAirportHourly ? AIRPORT_FLAT_FEE : (getPrice("hourly") || 0) * rentalHours)
    : (getPrice(rentalMode) || 0) * rentalDays;

  // --- Hour Pack auto-detection (hourly rentals only) ---
  // Mirrors backend logic: find the most advantageous active pack matching this vehicle
  // with enough hours_remaining.
  function _packMatchesVehicle(pack, veh) {
    const filter = pack?.vehicle_filter || {};
    if (filter.kind === "any") return true;
    if (filter.kind === "types") return (filter.vehicle_types || []).includes(veh?.vehicle_type);
    if (filter.kind === "price_max") return Number(veh?.price_per_hour || 0) <= Number(filter.max_price_per_hour || 0);
    return false;
  }
  function _packIsLive(pack) {
    try {
      if (Number(pack?.hours_remaining || 0) <= 0) return false;
      return new Date(pack.expires_at).getTime() > Date.now();
    } catch { return false; }
  }
  const eligiblePack = (() => {
    if (rentalMode !== "hourly" || !v || !Array.isArray(activePacks) || !activePacks.length) return null;
    const candidates = activePacks.filter((p) => _packIsLive(p) && Number(p.hours_remaining) >= rentalHours && _packMatchesVehicle(p, v));
    if (!candidates.length) return null;
    return candidates.reduce((best, p) => (best && Number(best.rate_per_hour) <= Number(p.rate_per_hour) ? best : p), null);
  })();
  const packAppliesToBooking = !!eligiblePack;
  const packTotal = eligiblePack ? Number(eligiblePack.rate_per_hour) * rentalHours : 0;

  // Welcome promo eligibility preview
  const promoExpiry = user?.promo_first_booking_expires_at ? new Date(user.promo_first_booking_expires_at) : null;
  const promoNotExpired = promoExpiry ? promoExpiry > new Date() : false;
  const meetsMinDuration = rentalMode === "hourly" ? rentalHours >= (user?.promo_first_booking_min_hours || 2) : !!rentalMode;
  const promoEligible = !!(
    welcomePromoActive &&
    user?.role === "passenger" &&
    !user?.promo_first_booking_used &&
    user?.promo_first_booking_discount_percent > 0 &&
    promoNotExpired &&
    meetsMinDuration &&
    totalAmount > 0 &&
    !packAppliesToBooking
  );
  const promoPercent = user?.promo_first_booking_discount_percent || 15;
  // Custom code takes priority over welcome promo. If applied, hide welcome promo entirely.
  const customCodeDiscount = promoCodeApplied ? Number(promoCodeApplied.discount || 0) : 0;
  const welcomePromoDiscount = (promoEligible && !promoCodeApplied) ? Math.round(totalAmount * promoPercent / 100) : 0;
  const promoDiscount = welcomePromoDiscount; // legacy alias for existing UI usages
  const finalAmount = packAppliesToBooking ? packTotal : totalAmount - welcomePromoDiscount - customCodeDiscount;

  const validatePromoCode = async () => {
    const raw = (promoCodeInput || "").trim().toUpperCase();
    if (!raw) return;
    if (!rentalMode || totalAmount <= 0) {
      setPromoCodeError("Choisissez d'abord un type de location");
      return;
    }
    setPromoCodeChecking(true); setPromoCodeError("");
    try {
      const { data } = await api.post("/promo-codes/preview", {
        code: raw,
        rental_mode: rentalMode,
        rental_days: rentalMode === "hourly" ? 0 : rentalDays,
        rental_hours: rentalMode === "hourly" ? rentalHours : 0,
        total_amount: totalAmount,
      });
      if (!data?.ok) {
        setPromoCodeApplied(null);
        setPromoCodeError(data?.error || "Code invalide");
        return;
      }
      setPromoCodeApplied({ code: data.code, name: data.name, percent: data.percent, discount: data.discount });
      setPromoCodeError("");
    } catch (e) {
      setPromoCodeApplied(null);
      setPromoCodeError(e.response?.data?.detail || "Erreur");
    } finally { setPromoCodeChecking(false); }
  };

  const clearPromoCode = () => {
    setPromoCodeInput(""); setPromoCodeApplied(null); setPromoCodeError("");
  };

  const isFormValid = () => {
    if (!rentalMode) return false;
    if (!isModeAvailable(rentalMode)) return false;
    if (rentalMode === "hourly") {
      if (rentalHours < 1) return false;
      if (!hourlyDest) return false;
      if (hourlyDest === "other" && !hourlyDestCustom.trim()) return false;
      return true;
    }
    // city_tour or out_of_city need days + dates + cities
    const depCity = departureCity === "_other" ? customDeparture : departureCity;
    const destCity = destinationCity === "_other" ? customDestination : destinationCity;
    return rentalDays >= 1 && startDate && endDate && depCity && destCity;
  };

  const handleRent = () => {
    if (!onRent) return;
    const depCity = departureCity === "_other" ? customDeparture : departureCity;
    const destCity = destinationCity === "_other" ? customDestination : destinationCity;
    onRent(v, {
      rentalMode, rentalHours, rentalDays,
      startDate, endDate, startTime, endTime, departureCity: depCity, destinationCity: destCity,
      pickupNeighborhood, pickupAddress,
      pickupGeo, dropoffGeo,
      paymentMethod,
      promoCode: promoCodeApplied?.code || "",
      hourlyDestination: rentalMode === "hourly" ? hourlyDest : "",
      hourlyDestinationCustom: rentalMode === "hourly" && hourlyDest === "other" ? hourlyDestCustom : "",
    });
  };

  const tftBalance = Number(user?.tft_balance || 0);
  const tftSufficient = tftBalance >= finalAmount;
  const tftEnabled = paymentMethods?.tft !== false; // default to true if prop not provided
  const cashbackRate = paymentMethods?.cashback_rate || 0;
  const cashbackAmount = Math.floor(finalAmount * cashbackRate);
  const showTftOption = !!(user?.role === "passenger" && rentalMode && finalAmount > 0 && tftEnabled && !packAppliesToBooking);

  return (
    <Dialog open={open !== undefined ? open : !!vehicle} onOpenChange={(val) => { if (!val) onClose(); }}>
      <DialogContent className="sm:max-w-2xl p-0 overflow-hidden max-h-[92vh]" data-testid="vehicle-detail-modal">
        {/* Image gallery */}
        <div className="relative bg-[#0A2540]">
          <img src={allImages[currentImg]} alt={`${v.brand} ${v.model}`} className="w-full h-64 sm:h-80 object-contain bg-[#0A2540]" data-testid="vehicle-detail-image" onError={(e) => { e.target.src = FALLBACK_IMAGES[v.vehicle_type] || FALLBACK_IMAGES.sedan; }} />
          {allImages.length > 1 && (
            <>
              <button onClick={prev} className="absolute left-2 top-1/2 -translate-y-1/2 w-9 h-9 bg-black/40 hover:bg-black/60 text-white rounded-full flex items-center justify-center backdrop-blur-sm"><ChevronLeft className="w-5 h-5" /></button>
              <button onClick={next} className="absolute right-2 top-1/2 -translate-y-1/2 w-9 h-9 bg-black/40 hover:bg-black/60 text-white rounded-full flex items-center justify-center backdrop-blur-sm"><ChevronRight className="w-5 h-5" /></button>
              <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
                {allImages.map((url, i) => (
                  <button key={`thumb-${i}`} onClick={() => setCurrentImg(i)} className={`w-12 h-8 rounded-md overflow-hidden border-2 transition-all ${i === currentImg ? "border-[#FACC15] scale-110" : "border-white/30 opacity-70"}`}>
                    <img src={url} alt="" className="w-full h-full object-cover" onError={(e) => { e.target.style.display = 'none'; }} />
                  </button>
                ))}
              </div>
            </>
          )}
        </div>

        {/* Details */}
        <div className="p-5 overflow-y-auto max-h-[50vh]">
          {/* Video presentation (large format, after gallery) */}
          {videoSrc && (
            <div className="mb-4 rounded-xl overflow-hidden border border-[#CBD5E1] bg-black" data-testid="vehicle-detail-video-wrapper">
              <div className="px-3 py-2 bg-[#0A2540] text-white text-xs font-semibold flex items-center gap-2">
                <span className="inline-block w-2 h-2 rounded-full bg-[#FACC15] animate-pulse" />
                Vidéo de présentation
              </div>
              <video
                src={videoSrc}
                controls
                playsInline
                preload="metadata"
                className="w-full max-h-[420px] bg-black"
                data-testid="vehicle-detail-video"
              >
                Votre navigateur ne supporte pas la vidéo HTML5.
              </video>
            </div>
          )}

          <div className="flex items-start justify-between mb-3">
            <div>
              <h2 className="text-xl font-bold text-[#0A2540]" style={{ fontFamily: 'Outfit' }}>{v.brand} {v.model}</h2>
              <p className="text-sm text-[#64748B]">{v.year} | {v.color} | {v.plate_number}</p>
            </div>
            <div className="text-right">
              <p className="text-2xl font-bold text-[#0A2540]" style={{ fontFamily: 'Outfit' }}>{v.price_per_day?.toLocaleString()}</p>
              <p className="text-xs text-[#64748B]">XAF/jour</p>
              {v.price_per_hour > 0 && <p className="text-sm font-semibold text-[#3B82F6]">{v.price_per_hour.toLocaleString()} XAF/heure</p>}
            </div>
          </div>

          {v.vehicle_condition && (
            <div className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full border text-xs font-semibold mb-3 ${conditionColor(v.vehicle_condition)}`}>
              <Star className="w-3 h-3" /> {conditionLabel(v.vehicle_condition)}
            </div>
          )}

          {/* Owner */}
          <div className="flex items-center gap-3 p-3 rounded-xl bg-[#F8FAFC] border border-[#CBD5E1] mb-4" data-testid="vehicle-detail-owner">
            {v.owner_photo ? (
              <img src={resolveImageUrl(v.owner_photo)} alt={v.owner_name} className="w-16 h-16 rounded-full object-cover border-2 border-[#FACC15]" />
            ) : (
              <div className="w-16 h-16 rounded-full bg-[#0A2540] flex items-center justify-center"><User className="w-8 h-8 text-[#FACC15]" /></div>
            )}
            <div className="flex-1">
              <p className="text-sm font-semibold text-[#0A2540]">{v.owner_name}</p>
              <p className="text-xs text-[#64748B]">Proprietaire{v.city ? ` - ${v.city}` : ""}</p>
            </div>
          </div>

          {/* Features */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3">
            <FeatureCard icon={<Users className="w-4 h-4" />} label="Places" value={`${v.seats || 4} places`} />
            <FeatureCard icon={<Calendar className="w-4 h-4" />} label="Type" value={VEHICLE_TYPE_LABELS[v.vehicle_type] || v.vehicle_type} />
            {v.mileage && <FeatureCard icon={<Gauge className="w-4 h-4" />} label="Kilometrage" value={v.mileage} />}
            <FeatureCard icon={<Fuel className="w-4 h-4" />} label="Carburant" value={v.fuel_policy === "included" ? "Inclus" : "Non inclus"} highlight={v.fuel_policy === "included"} />
          </div>

          <div className="flex flex-wrap gap-2 mb-4">
            {v.air_conditioning && <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#10B981]/10 text-[#10B981] border border-[#10B981]/20"><ThermometerSun className="w-3 h-3 inline mr-0.5" />Clim</span>}
            {v.music && <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#10B981]/10 text-[#10B981] border border-[#10B981]/20"><Music className="w-3 h-3 inline mr-0.5" />Musique</span>}
            {v.phone_charger && <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#10B981]/10 text-[#10B981] border border-[#10B981]/20"><BatteryCharging className="w-3 h-3 inline mr-0.5" />Chargeur</span>}
          </div>

          {/* Rental mode preview (Explorer / read-only) */}
          {!onRent && !showRentButton && (
            <>
              <p className="text-sm font-extrabold text-[#0A2540] mb-3" style={{ fontFamily: "Outfit" }}>Type de location</p>
              <div className="grid grid-cols-2 gap-3 mb-4" data-testid="rental-mode-preview">
                {RENTAL_MODES.map(m => {
                  const price = getPrice(m.id);
                  const available = typeof price === "number" && price > 0;
                  return (
                    <div
                      key={m.id}
                      className={`p-4 rounded-2xl ${
                        available
                          ? "bg-white border border-[#E2E8F0]"
                          : "bg-[#F1F5F9] border border-[#E2E8F0]"
                      }`}
                      data-testid={`rental-mode-preview-${m.id}`}
                    >
                      <span className={`w-9 h-9 rounded-full flex items-center justify-center mb-2.5 ${available ? "bg-[#FACC15] text-[#0A2540]" : "bg-white text-[#94A3B8]"}`}>
                        <m.icon className="w-4 h-4" />
                      </span>
                      <p className={`text-sm font-extrabold leading-tight ${available ? "text-[#0A2540]" : "text-[#94A3B8]"}`} style={{ fontFamily: "Outfit" }}>
                        {m.label}
                      </p>
                      {available ? (
                        <p className="text-xs mt-1 text-[#64748B]">
                          <strong className="font-bold text-[#0A2540]">{price.toLocaleString("fr-FR")}</strong> FCFA{m.suffix}
                        </p>
                      ) : (
                        <p className="text-[11px] italic mt-1 text-[#94A3B8]">Non applicable</p>
                      )}
                    </div>
                  );
                })}
              </div>
              <div className="mb-4 p-3 rounded-2xl bg-[#FEF3C7] border border-[#FACC15]/50 flex items-center gap-2.5" data-testid="explorer-preview-notice">
                <Eye className="w-4 h-4 text-[#0A2540] shrink-0" />
                <p className="text-xs text-[#92400E] leading-snug">
                  <strong className="text-[#0A2540]">Mode Explorer</strong> — connectez-vous pour choisir un type de location et réserver ce véhicule.
                </p>
              </div>
            </>
          )}

          {/* Rental mode selection - only when renting */}
          {(onRent || showRentButton) && (
            <>
              <p className="text-sm font-extrabold text-[#0A2540] mb-3" style={{ fontFamily: "Outfit" }}>Type de location</p>
              <div className="grid grid-cols-2 gap-3 mb-4" data-testid="rental-mode-selection">
                {RENTAL_MODES.map(m => {
                  const price = getPrice(m.id);
                  const available = typeof price === "number" && price > 0;
                  const isActive = rentalMode === m.id;
                  return (
                    <button
                      key={m.id}
                      type="button"
                      disabled={!available}
                      onClick={() => available && setRentalMode(m.id)}
                      className={`relative p-4 rounded-2xl text-left transition-all ${
                        isActive
                          ? "bg-[#0A2540] text-white shadow-lg"
                          : available
                          ? "bg-white border border-[#E2E8F0] text-[#0A2540] hover:border-[#0A2540]"
                          : "bg-[#F1F5F9] border border-[#E2E8F0] text-[#94A3B8] cursor-not-allowed"
                      }`}
                      data-testid={`rental-mode-${m.id}`}
                      title={available ? "" : "Tarif non renseigné par le chauffeur"}
                    >
                      <span
                        className={`w-9 h-9 rounded-full flex items-center justify-center mb-2.5 ${
                          isActive ? "bg-[#FACC15] text-[#0A2540]" : "bg-[#F1F5F9] text-[#0A2540]"
                        }`}
                      >
                        <m.icon className="w-4 h-4" />
                      </span>
                      <p className={`text-sm font-extrabold leading-tight ${isActive ? "text-white" : "text-[#0A2540]"}`} style={{ fontFamily: "Outfit" }}>
                        {m.label}
                      </p>
                      {available ? (
                        <p className={`text-xs mt-1 ${isActive ? "text-[#FACC15]" : "text-[#64748B]"}`}>
                          <strong className="font-bold">{price.toLocaleString("fr-FR")}</strong> FCFA{m.suffix}
                        </p>
                      ) : (
                        <p className="text-[11px] italic mt-1">Non applicable</p>
                      )}
                    </button>
                  );
                })}
              </div>

              {/* Hourly mode: choose hours + date */}
              {rentalMode === "hourly" && (
                <div className="mb-4 p-3 rounded-xl bg-[#F8FAFC] border border-[#CBD5E1]" data-testid="hourly-options">
                  <label className="text-xs font-medium text-[#0A2540] block mb-1">Nombre d&apos;heures</label>
                  <Select value={String(rentalHours)} onValueChange={v => setRentalHours(parseInt(v))}>
                    <SelectTrigger className="h-9 border-[#CBD5E1]" data-testid="select-hours"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {[1,2,3,4,5,6].map(h => <SelectItem key={h} value={String(h)}>{h} heure{h > 1 ? "s" : ""}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <div className="mt-2">
                    <label className="text-[10px] text-[#64748B] font-medium block mb-1">Date de réservation</label>
                    <Input type="date" value={startDate} onChange={e => { setStartDate(e.target.value); setEndDate(e.target.value); }} className="h-9 text-xs border-[#CBD5E1] bg-white" data-testid="hourly-date" />
                  </div>
                  <div className="grid grid-cols-2 gap-2 mt-2">
                    <div>
                      <label className="text-[10px] text-[#64748B] font-medium block mb-1 flex items-center gap-1"><Clock className="w-3 h-3" /> Heure de départ</label>
                      <Input type="time" value={startTime} onChange={e => setStartTime(e.target.value)} className="h-9 text-xs border-[#CBD5E1] bg-white" data-testid="hourly-start-time" />
                    </div>
                    <div>
                      <label className="text-[10px] text-[#64748B] font-medium block mb-1 flex items-center gap-1"><Clock className="w-3 h-3" /> Heure de retour</label>
                      <Input type="time" value={endTime} onChange={e => setEndTime(e.target.value)} className="h-9 text-xs border-[#CBD5E1] bg-white" data-testid="hourly-end-time" />
                    </div>
                  </div>
                  {/* Destination presets (Airport → 20 000 F flat) */}
                  <div className="mt-3">
                    <label className="text-[10px] text-[#64748B] font-medium block mb-1">Destination</label>
                    <Select value={hourlyDest || "placeholder"} onValueChange={(v) => setHourlyDest(v === "placeholder" ? "" : v)}>
                      <SelectTrigger className="h-9 border-[#CBD5E1] text-xs" data-testid="hourly-destination"><SelectValue placeholder="Choisir la destination" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="placeholder" disabled>Choisir la destination</SelectItem>
                        <SelectItem value="airport_douala">✈️ Aéroport de Douala · 20 000 F fixe</SelectItem>
                        <SelectItem value="airport_yaounde">✈️ Aéroport de Yaoundé · 20 000 F fixe</SelectItem>
                        <SelectItem value="other">📍 Autre destination</SelectItem>
                      </SelectContent>
                    </Select>
                    {hourlyDest === "other" && (
                      <Input value={hourlyDestCustom} onChange={(e) => setHourlyDestCustom(e.target.value)}
                        placeholder="Précisez votre destination" className="h-9 mt-2 text-xs border-[#CBD5E1] bg-white"
                        data-testid="hourly-destination-custom" />
                    )}
                    {isAirportHourly && (
                      <p className="text-[10px] text-[#065F46] font-semibold mt-1.5">
                        ✓ Forfait aéroport 20 000 F · le tarif horaire ne s&apos;applique pas
                      </p>
                    )}
                  </div>
                  {/* Overtime notice */}
                  <p className="text-[10px] text-[#64748B] mt-2 leading-snug">
                    💡 Dépassement : 1 000 F les 15 premières min · 900 F les 15 min suivantes · 800 F par tranche de 15 min au-delà. Le chrono démarre dès la saisie de l&apos;OTP par le chauffeur.
                  </p>
                </div>
              )}
              {(rentalMode === "city_tour" || rentalMode === "out_of_city") && (
                <div className="mb-4 p-3 rounded-xl bg-[#F8FAFC] border border-[#CBD5E1] space-y-3" data-testid="rental-details-form">
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="text-[10px] text-[#64748B] font-medium block mb-1">Ville de depart</label>
                      <Select value={departureCity || "placeholder"} onValueChange={v => setDepartureCity(v === "placeholder" ? "" : v)}>
                        <SelectTrigger className="h-9 border-[#CBD5E1] text-xs" data-testid="departure-city"><SelectValue placeholder="Choisir" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="placeholder" disabled>Choisir la ville</SelectItem>
                          {CAMEROON_CITIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                          <SelectItem value="_other">Autre...</SelectItem>
                        </SelectContent>
                      </Select>
                      {departureCity === "_other" && <Input value={customDeparture} onChange={e => setCustomDeparture(e.target.value)} placeholder="Saisir la ville" className="h-8 mt-1 text-xs border-[#CBD5E1]" data-testid="custom-departure" />}
                    </div>
                    <div>
                      <label className="text-[10px] text-[#64748B] font-medium block mb-1">Ville destination</label>
                      <Select value={destinationCity || "placeholder"} onValueChange={v => setDestinationCity(v === "placeholder" ? "" : v)}>
                        <SelectTrigger className="h-9 border-[#CBD5E1] text-xs" data-testid="destination-city"><SelectValue placeholder="Choisir" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="placeholder" disabled>Choisir la ville</SelectItem>
                          {CAMEROON_CITIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                          <SelectItem value="_other">Autre...</SelectItem>
                        </SelectContent>
                      </Select>
                      {destinationCity === "_other" && <Input value={customDestination} onChange={e => setCustomDestination(e.target.value)} placeholder="Saisir la ville" className="h-8 mt-1 text-xs border-[#CBD5E1]" data-testid="custom-destination" />}
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    <div>
                      <label className="text-[10px] text-[#64748B] font-medium block mb-1">Date debut</label>
                      <Input type="date" value={startDate} onChange={e => { setStartDate(e.target.value); if (endDate && e.target.value) { const d1 = new Date(e.target.value); const d2 = new Date(endDate); const diff = Math.max(1, Math.ceil((d2-d1)/(86400000))); setRentalDays(diff); }}} className="h-9 text-xs border-[#CBD5E1]" data-testid="start-date" />
                    </div>
                    <div>
                      <label className="text-[10px] text-[#64748B] font-medium block mb-1">Date fin</label>
                      <Input type="date" value={endDate} onChange={e => { setEndDate(e.target.value); if (startDate && e.target.value) { const d1 = new Date(startDate); const d2 = new Date(e.target.value); const diff = Math.max(1, Math.ceil((d2-d1)/(86400000))); setRentalDays(diff); }}} className="h-9 text-xs border-[#CBD5E1]" data-testid="end-date" />
                    </div>
                    <div>
                      <label className="text-[10px] text-[#64748B] font-medium block mb-1">Jours</label>
                      <Input type="number" value={rentalDays} onChange={e => setRentalDays(Math.max(1, parseInt(e.target.value) || 1))} min={1} className="h-9 text-xs border-[#CBD5E1]" data-testid="rental-days" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="text-[10px] text-[#64748B] font-medium block mb-1 flex items-center gap-1"><Clock className="w-3 h-3" /> Heure de départ</label>
                      <Input type="time" value={startTime} onChange={e => setStartTime(e.target.value)} className="h-9 text-xs border-[#CBD5E1]" data-testid="start-time" />
                    </div>
                    <div>
                      <label className="text-[10px] text-[#64748B] font-medium block mb-1 flex items-center gap-1"><Clock className="w-3 h-3" /> Heure de retour</label>
                      <Input type="time" value={endTime} onChange={e => setEndTime(e.target.value)} className="h-9 text-xs border-[#CBD5E1]" data-testid="end-time" />
                    </div>
                  </div>
                </div>
              )}

              {/* Pickup precision (passenger-visible to driver) */}
              {rentalMode && (
                <div className="mb-4 bg-[#FACC15]/10 border border-[#FACC15]/30 rounded-xl p-3 space-y-2" data-testid="pickup-details-section">
                  <div className="flex items-center gap-2">
                    <MapPin className="w-3.5 h-3.5 text-[#0A2540]" />
                    <p className="text-xs font-semibold text-[#0A2540] uppercase tracking-wide">Lieu de prise en charge</p>
                  </div>
                  <p className="text-[10px] text-[#64748B] -mt-1">Recherchez votre adresse ou utilisez votre position GPS. Le chauffeur pourra vous localiser avec précision.</p>
                  <APIProvider apiKey={MAPS_KEY} libraries={["places", "geocoding", "routes"]}>
                    <LocationPicker
                      value={pickupGeo}
                      onChange={(picked) => {
                        setPickupGeo(picked);
                        if (picked?.address) setPickupAddress(picked.address);
                      }}
                      placeholder="Ex: Carrefour Bastos, en face du Total…"
                      label="Adresse de prise en charge"
                      testId="pickup-location-picker"
                      pinColor="#0A2540"
                      savePresetIcon="home"
                      autoGpsOnMount
                    />
                    {/* Destination geolocation - key info for the driver and
                        for the live-tracking link the passenger shares. Shown
                        for every rental mode so relatives can watch the ride
                        converge to the real destination on the public map. */}
                    <div className="pt-2">
                      <LocationPicker
                        value={dropoffGeo}
                        onChange={setDropoffGeo}
                        placeholder="Ex: Douala centre-ville, Marché central…"
                        label="Adresse de destination"
                        testId="dropoff-location-picker"
                        bias={pickupGeo}
                        pinColor="#FACC15"
                        savePresetIcon="airport"
                      />
                    </div>
                    {/* Live route summary (informational only — rental price is time-based). */}
                    {pickupGeo?.lat && dropoffGeo?.lat && (
                      <RouteInfoBadge
                        pickup={pickupGeo}
                        dropoff={dropoffGeo}
                        onRoute={setRouteInfo}
                        hint={rentalMode === "out_of_city" && routeInfo?.distance_km ? `${(routeInfo.distance_km * 2).toFixed(0)} km aller-retour` : null}
                      />
                    )}
                  </APIProvider>
                  <div className="grid sm:grid-cols-2 gap-2">
                    <div>
                      <label className="text-[10px] text-[#64748B] font-medium block mb-1">Quartier (optionnel)</label>
                      <Input value={pickupNeighborhood} onChange={e => setPickupNeighborhood(e.target.value)} placeholder="Ex: Bastos, Bonapriso..." className="h-9 text-xs border-[#CBD5E1] bg-white" data-testid="pickup-neighborhood-input" maxLength={80} />
                    </div>
                    <div>
                      <label className="text-[10px] text-[#64748B] font-medium block mb-1">Point de repère précis (optionnel)</label>
                      <Input value={pickupAddress} onChange={e => setPickupAddress(e.target.value)} placeholder="Ex: en face de la pharmacie centrale" className="h-9 text-xs border-[#CBD5E1] bg-white" data-testid="pickup-address-input" maxLength={200} />
                    </div>
                  </div>
                </div>
              )}

              {/* Total */}
              {rentalMode && (
                <div className="bg-[#0A2540] rounded-xl p-4 mb-4 space-y-2" data-testid="rental-total">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs text-white/60">{(promoEligible || packAppliesToBooking) ? "Sous-total" : "Total estimé"}</p>
                      <p className="text-xs text-white/40">
                        {rentalMode === "hourly" ? `${rentalHours}h × ${getPrice("hourly").toLocaleString()} XAF` : `${rentalDays}j × ${getPrice(rentalMode).toLocaleString()} XAF`}
                      </p>
                    </div>
                    <p className={`text-${(promoEligible || packAppliesToBooking) ? "lg" : "2xl"} font-bold text-${(promoEligible || packAppliesToBooking) ? "white/70" : "[#FACC15]"} ${(promoEligible || packAppliesToBooking) ? "line-through opacity-70" : ""}`} style={{ fontFamily: 'Outfit' }}>
                      {totalAmount.toLocaleString()} XAF
                    </p>
                  </div>
                  {packAppliesToBooking && (
                    <>
                      <div className="flex items-center justify-between text-xs text-[#FACC15]" data-testid="pack-applied-line">
                        <span className="inline-flex items-center gap-1">
                          <Hourglass className="w-3 h-3" /> Pack {eligiblePack.type} appliqué ({eligiblePack.rate_per_hour.toLocaleString()} F/h × {rentalHours}h)
                        </span>
                        <span className="font-semibold">−{(totalAmount - packTotal).toLocaleString()} XAF</span>
                      </div>
                      <div className="border-t border-white/10 pt-2 flex items-center justify-between">
                        <p className="text-xs text-white/80 font-medium">Vous payez (via Pack {eligiblePack.type})</p>
                        <p className="text-2xl font-bold text-[#FACC15]" style={{ fontFamily: 'Outfit' }} data-testid="rental-final-amount">
                          {finalAmount.toLocaleString()} XAF
                        </p>
                      </div>
                      <p className="text-[10px] text-white/60" data-testid="pack-deduction-info">
                        −{rentalHours}h débitées de votre Pack {eligiblePack.type} · {Number(eligiblePack.hours_remaining) - rentalHours}h restantes après réservation
                      </p>
                    </>
                  )}
                  {promoEligible && !packAppliesToBooking && !promoCodeApplied && (
                    <>
                      <div className="flex items-center justify-between text-xs text-[#FACC15]" data-testid="promo-discount-line">
                        <span className="inline-flex items-center gap-1">
                          <Sparkles className="w-3 h-3" /> Promo bienvenue WELCOME15 (-{promoPercent}%)
                        </span>
                        <span className="font-semibold">−{promoDiscount.toLocaleString()} XAF</span>
                      </div>
                    </>
                  )}
                  {promoCodeApplied && !packAppliesToBooking && (
                    <div className="flex items-center justify-between text-xs text-[#FACC15]" data-testid="promo-code-discount-line">
                      <span className="inline-flex items-center gap-1">
                        <Sparkles className="w-3 h-3" /> Code {promoCodeApplied.code} (-{promoCodeApplied.percent}%)
                      </span>
                      <span className="font-semibold">−{Number(promoCodeApplied.discount).toLocaleString()} XAF</span>
                    </div>
                  )}
                  {((promoEligible && !packAppliesToBooking) || promoCodeApplied) && (
                    <div className="border-t border-white/10 pt-2 flex items-center justify-between">
                      <p className="text-xs text-white/80 font-medium">Vous payez</p>
                      <p className="text-2xl font-bold text-[#FACC15]" style={{ fontFamily: 'Outfit' }} data-testid="rental-final-amount">
                        {finalAmount.toLocaleString()} XAF
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* Promo code input (passenger, when a rental mode is selected and no pack applies) */}
              {user?.role === "passenger" && rentalMode && totalAmount > 0 && !packAppliesToBooking && (
                <div className="mb-4 p-3 rounded-xl bg-[#FEFCE8] border border-[#FACC15]/40" data-testid="promo-code-input-section">
                  <p className="text-xs font-bold text-[#0A2540] uppercase tracking-wide mb-2 flex items-center gap-1">
                    <Sparkles className="w-3.5 h-3.5" /> Vous avez un code promo ?
                  </p>
                  {!promoCodeApplied ? (
                    <>
                      <div className="flex gap-2">
                        <input
                          value={promoCodeInput}
                          onChange={(e) => { setPromoCodeInput(e.target.value.toUpperCase().replace(/[^A-Z0-9_-]/g, "")); setPromoCodeError(""); }}
                          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); validatePromoCode(); } }}
                          placeholder="ex. SUMMER20"
                          className="flex-1 px-3 h-10 rounded-lg border border-[#CBD5E1] font-mono uppercase text-sm focus:outline-none focus:ring-2 focus:ring-[#FACC15]"
                          data-testid="promo-code-user-input"
                        />
                        <button
                          type="button"
                          onClick={validatePromoCode}
                          disabled={promoCodeChecking || !promoCodeInput}
                          className="px-4 h-10 rounded-lg bg-[#0A2540] text-white font-semibold text-sm hover:bg-[#081b2e] disabled:opacity-50"
                          data-testid="promo-code-apply-btn"
                        >
                          {promoCodeChecking ? "…" : "Appliquer"}
                        </button>
                      </div>
                      {promoCodeError && (
                        <p className="text-xs text-[#EF4444] mt-1.5" data-testid="promo-code-error">{promoCodeError}</p>
                      )}
                    </>
                  ) : (
                    <div className="flex items-center justify-between p-2 rounded-lg bg-white border border-[#10B981]/40" data-testid="promo-code-applied-banner">
                      <div>
                        <p className="text-sm font-bold text-[#10B981]">✓ Code {promoCodeApplied.code} appliqué</p>
                        {promoCodeApplied.name && <p className="text-[10px] text-[#64748B]">{promoCodeApplied.name} · -{promoCodeApplied.percent}%</p>}
                      </div>
                      <button
                        type="button"
                        onClick={clearPromoCode}
                        className="text-xs text-[#EF4444] hover:underline"
                        data-testid="promo-code-clear-btn"
                      >
                        Retirer
                      </button>
                    </div>
                  )}
                </div>
              )}

              {/* Payment method (passenger only) */}
              {showTftOption && (
                <div className="mb-4 space-y-2" data-testid="payment-method-section">
                  <p className="text-xs font-semibold text-[#0A2540] uppercase tracking-wide">Mode de paiement</p>
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      type="button"
                      onClick={() => setPaymentMethod("")}
                      className={`p-3 rounded-xl border-2 text-left transition-all ${paymentMethod === "" ? "border-[#FACC15] bg-[#FACC15]/5" : "border-[#CBD5E1] hover:border-[#FACC15]/40"}`}
                      data-testid="pay-cash-option"
                    >
                      <div className="flex items-center gap-2 mb-0.5">
                        <Banknote className="w-4 h-4 text-[#0A2540]" />
                        <p className="text-xs font-bold text-[#0A2540]">Paiement à la prise en charge</p>
                      </div>
                      <p className="text-[10px] text-[#64748B]">Cash ou Mobile Money au chauffeur</p>
                    </button>
                    <button
                      type="button"
                      onClick={() => tftSufficient && setPaymentMethod("tft")}
                      disabled={!tftSufficient}
                      className={`p-3 rounded-xl border-2 text-left transition-all ${paymentMethod === "tft" ? "border-[#FACC15] bg-[#FACC15]/5" : "border-[#CBD5E1] hover:border-[#FACC15]/40"} ${!tftSufficient ? "opacity-60 cursor-not-allowed" : "cursor-pointer"}`}
                      data-testid="pay-tft-option"
                      title={tftSufficient ? "Payer instantanément via votre portefeuille TFT" : "Solde TFT insuffisant"}
                    >
                      <div className="flex items-center gap-2 mb-0.5">
                        <Wallet className="w-4 h-4 text-[#0A2540]" />
                        <p className="text-xs font-bold text-[#0A2540]">Portefeuille TFT</p>
                      </div>
                      <p className="text-[10px] text-[#64748B]" data-testid="tft-balance-info">
                        Solde: <span className={tftSufficient ? "text-[#10B981] font-semibold" : "text-[#EF4444] font-semibold"}>{tftBalance.toLocaleString()} TFT</span>
                      </p>
                    </button>
                  </div>
                  {paymentMethod === "tft" && tftSufficient && (
                    <p className="text-[11px] text-[#10B981] flex items-center gap-1" data-testid="tft-pay-confirm">
                      <Sparkles className="w-3 h-3" /> {finalAmount.toLocaleString()} TFT seront débités.{cashbackAmount > 0 && <> Vous recevrez <strong>+{cashbackAmount.toLocaleString()} TFT</strong> de cashback ({(cashbackRate * 100).toFixed(0)}%).</>} Solde après: {(tftBalance - finalAmount + cashbackAmount).toLocaleString()} TFT.
                    </p>
                  )}
                  {!tftSufficient && (
                    <p className="text-[11px] text-[#EF4444]" data-testid="tft-insufficient-msg">
                      Solde TFT insuffisant ({tftBalance.toLocaleString()} TFT). Il vous manque {(finalAmount - tftBalance).toLocaleString()} TFT. <span className="text-[#0A2540] font-semibold">Rechargez via l&apos;onglet Portefeuille.</span>
                    </p>
                  )}
                </div>
              )}

              {/* Action */}
              {onRent && (
                <Button onClick={handleRent} disabled={loading || !isFormValid()} className="w-full h-14 bg-[#FACC15] text-[#0A2540] hover:bg-[#EAB308] font-extrabold text-base rounded-full shadow-lg shadow-[#FACC15]/25 disabled:opacity-50" data-testid="vehicle-detail-rent-btn">
                  {!rentalMode ? "Choisissez un type de location" : !isFormValid() ? "Complétez les informations" : packAppliesToBooking ? `Payer ${finalAmount.toLocaleString()} F via Pack ${eligiblePack.type}` : paymentMethod === "tft" ? `Payer ${finalAmount.toLocaleString()} TFT et louer` : "Louer ce véhicule"}
                </Button>
              )}
              {!onRent && showRentButton && (
                <Link to={`/passenger?rent_vehicle=${v.id}`} className="block">
                  <Button className="w-full h-14 bg-[#FACC15] text-[#0A2540] hover:bg-[#EAB308] font-extrabold text-base rounded-full shadow-lg shadow-[#FACC15]/25">Louer ce véhicule</Button>
                </Link>
              )}
            </>
          )}

          {/* Explorer preview CTA (rendered outside the auth-only wrapper) */}
          {!onRent && !showRentButton && (
            <Link to="/login" className="block" state={{ next: "/vehicles" }}>
              <Button className="w-full h-14 bg-[#FACC15] text-[#0A2540] hover:bg-[#EAB308] font-extrabold text-base rounded-full shadow-lg shadow-[#FACC15]/25 inline-flex items-center justify-center gap-2" data-testid="vehicle-detail-login-cta">
                <LogIn className="w-4 h-4" /> Se connecter pour réserver
              </Button>
            </Link>
          )}

          {/* Back to list — only when the modal was auto-opened from a shared link */}
          {openedFromShareLink && (
            <button
              type="button"
              onClick={handleBackToList}
              className="mt-3 w-full inline-flex items-center justify-center gap-2 h-12 rounded-full border border-[#0A2540]/15 text-[#0A2540] font-semibold text-sm hover:bg-[#0A2540] hover:text-white transition"
              data-testid="vehicle-detail-back-to-list"
            >
              <ArrowLeft className="w-4 h-4" /> Retour à la liste des véhicules
            </button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
